import { type CSSProperties, type ReactNode, useEffect, useRef, useState } from 'react';
import { LOCALES, type Locale, setLocale, t } from '@app/i18n';
import { useLocale } from '@app/i18n/useLocale';
import { Icon, type IconName } from '../theme/Icon';
import { useColorMode, type ColorModePreference } from '../theme/colorMode';
import { InfoTip } from './InfoTip';
import s from './Topbar.module.css';

/** Opções do alternador de tema, na ordem em que aparecem no menu do usuário. */
const TEMAS: ReadonlyArray<{ valor: ColorModePreference; chave: string; icon: IconName }> = [
  { valor: 'light', chave: 'common.theme.light', icon: 'sun' },
  { valor: 'dark', chave: 'common.theme.dark', icon: 'moon' },
  { valor: 'system', chave: 'common.theme.system', icon: 'switch' },
];

/**
 * Claro · escuro · sistema, dentro do menu do usuário — que é onde se procura.
 *
 * O menu **não** fecha na escolha, de propósito: a troca é instantânea e vale
 * ver o resultado antes de sair. A preferência persiste em `localStorage`
 * (`AimThemeProvider`) e é aplicada antes da primeira pintura pelo script do
 * `index.html`.
 */
function SeletorDeTema() {
  const { preference, setPreference } = useColorMode();

  return (
    <div className={s.umPref} role="group" aria-label={t('common.theme.label')}>
      <span className={s.umPrefRotulo}>{t('common.theme.label')}</span>
      <div className={s.umPrefOpcoes} style={{ '--pref-cols': TEMAS.length } as CSSProperties}>
        {TEMAS.map(({ valor, chave, icon }) => (
          <button
            key={valor}
            type="button"
            className={[s.umPrefBtn, preference === valor ? s.umPrefOn : '']
              .filter(Boolean)
              .join(' ')}
            aria-pressed={preference === valor}
            onClick={() => setPreference(valor)}
          >
            <Icon name={icon} size="sm" aria-hidden="true" />
            {t(chave)}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * O nome de cada idioma **no próprio idioma** (autônimo), e por isso fora do
 * catálogo: quem procura o seletor não lê o idioma corrente — é justamente
 * por não o ler que está procurando. «Portuguese» numa tela em inglês não ajuda
 * quem só fala português.
 *
 * Sem bandeiras, também de propósito: bandeira é país, não idioma.
 */
const IDIOMAS: Record<Locale, string> = {
  'pt-BR': 'Português',
  en: 'English',
};

/**
 * Seletor de idioma (Onda C · Lote 2a), irmão do de tema e no mesmo lugar.
 *
 * **A preferência mora no `localStorage`**, e isso é uma decisão de hoje, não o
 * desenho final: a API não tem campo de idioma no usuário. Quando tiver, o
 * servidor vira a fonte e este componente não muda — quem muda é `loadLocale()`
 * no `@app/i18n`.
 *
 * O `useLocale()` aqui não é do seletor: ele repinta o **console inteiro** na
 * troca, porque `t()` é função de módulo e não avisa o React sozinho.
 */
function SeletorDeIdioma() {
  const atual = useLocale();

  return (
    <div className={s.umPref} role="group" aria-label={t('common.language.label')}>
      <span className={s.umPrefRotulo}>{t('common.language.label')}</span>
      <div className={s.umPrefOpcoes} style={{ '--pref-cols': LOCALES.length } as CSSProperties}>
        {LOCALES.map((valor) => (
          <button
            key={valor}
            type="button"
            lang={valor}
            className={[s.umPrefBtn, atual === valor ? s.umPrefOn : ''].filter(Boolean).join(' ')}
            aria-pressed={atual === valor}
            onClick={() => setLocale(valor)}
          >
            {IDIOMAS[valor]}
          </button>
        ))}
      </div>
    </div>
  );
}

/** Estados do middleware no chip da topbar (`Shell.middleware.estado`, FR-013). */
export type MiddlewareState = 'alcancavel' | 'fora' | 'nao_configurado';

/**
 * Rótulo e tooltip do chip (FR-013) — funções, não tabelas: um `Record` de texto
 * é avaliado no import e congela o idioma da primeira pintura. Foram as duas
 * últimas tabelas congeladas do console.
 */
function mwLabel(estado: MiddlewareState): string {
  return t(`common.ui.middleware.${estado}`);
}

/**
 * `mwTipBase`, e não `mwTip`: o componente já tem um `mwTip` — o texto final,
 * com o complemento que o `ShellTopbar` manda concatenado.
 */
function mwTipBase(estado: MiddlewareState): string {
  return t(`common.ui.middleware.${estado}.tip`);
}

export interface TopbarMiddleware {
  estado: MiddlewareState;
  versao?: string | null;
  /**
   * Complemento do tooltip com URL, última sincronização e versão (FR-013).
   * Quem monta é o `ShellTopbar` — a barra não formata data nem conhece a API.
   */
  detalhe?: string | null;
}

export interface TopbarUser {
  nome: string;
  papel: string;
  email?: string | null;
  /** Conta Google vinculada (FR-015). `undefined` = não sabemos ainda. */
  googleVinculado?: boolean;
}

export interface TopbarProps {
  middleware?: TopbarMiddleware;
  tenant?: { cliente: string; ambiente: string };
  user?: TopbarUser;
  /** Abre a busca global (T2, FR-014). */
  onSearch?: () => void;
  /** Atalho exibido no campo de busca. */
  searchShortcut?: string;
  notificationsCount?: number;
  onNotifications?: () => void;
  /** **Sair** do menu do usuário (FR-008). Sem ele o item não aparece. */
  onLogout?: () => void;
  /** `true` enquanto o logout está em voo. */
  logoutPending?: boolean;
  /** Ações extras à direita (ex.: alternar tema). */
  children?: ReactNode;
}

function initials(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

/** Barra superior (`.top`): busca global, estado do middleware, tenant e usuário. */
export function Topbar({
  middleware,
  tenant,
  user,
  onSearch,
  searchShortcut = 'Ctrl K',
  notificationsCount = 0,
  onNotifications,
  onLogout,
  logoutPending = false,
  children,
}: TopbarProps) {
  const [menuAberto, setMenuAberto] = useState(false);
  // Tela cheia (T2): o estado vem do documento, não do clique — Esc e F11
  // saem sem passar por aqui.
  const [telaCheia, setTelaCheia] = useState(false);
  useEffect(() => {
    const sincronizar = () => setTelaCheia(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', sincronizar);
    return () => document.removeEventListener('fullscreenchange', sincronizar);
  }, []);
  const alternarScreenCheia = () => {
    if (document.fullscreenElement) void document.exitFullscreen?.();
    else void document.documentElement.requestFullscreen?.();
  };
  const userWrapRef = useRef<HTMLDivElement>(null);

  // Fecha o menu do usuário no Esc e no clique fora (nenhum dos dois é "escolher
  // um item", então não disparam nada além do fechamento).
  useEffect(() => {
    if (!menuAberto) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuAberto(false);
    };
    const onPointerDown = (e: MouseEvent) => {
      if (!userWrapRef.current?.contains(e.target as Node)) setMenuAberto(false);
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [menuAberto]);

  const mwClass =
    middleware?.estado === 'alcancavel'
      ? s.mwOk
      : middleware?.estado === 'fora'
        ? s.mwDown
        : s.mwUnset;

  const mwText = middleware
    ? middleware.estado === 'alcancavel' && middleware.versao
      ? t('common.ui.middleware.withVersion', {
          state: mwLabel('alcancavel'),
          version: middleware.versao,
        })
      : mwLabel(middleware.estado)
    : '';

  const mwTip = middleware
    ? [mwTipBase(middleware.estado), middleware.detalhe].filter(Boolean).join(' ')
    : '';

  return (
    <header className={s.top}>
      <button type="button" className={s.searchbar} onClick={onSearch}>
        <Icon name="search" size="sm" />
        <span className={s.searchLabel}>{t('common.ui.globalSearch')}</span>
        <span className={s.gskbd}>{searchShortcut}</span>
      </button>

      {middleware ? (
        <span className={[s.mwChip, mwClass].join(' ')} data-estado={middleware.estado}>
          <Icon name={middleware.estado === 'alcancavel' ? 'check-circle' : 'warning'} size="xs" />
          <span className="sr-only">{t('common.ui.middlewarePrefix')}</span>
          {mwText}
          <InfoTip text={mwTip} label={t('common.ui.about.middleware')} />
        </span>
      ) : null}

      {children}

      <button
        type="button"
        className={s.tbtn}
        onClick={alternarScreenCheia}
        aria-label={t(telaCheia ? 'common.ui.fullscreen.exit' : 'common.ui.fullscreen')}
        aria-pressed={telaCheia}
      >
        <Icon name={telaCheia ? 'compress' : 'expand'} />
      </button>

      <button
        type="button"
        className={s.tbtn}
        onClick={onNotifications}
        aria-label={
          notificationsCount > 0
            ? t('common.ui.notifications.count', { n: notificationsCount })
            : t('common.ui.notifications')
        }
      >
        <Icon name="bell" />
        {notificationsCount > 0 ? <span className={s.pip} aria-hidden="true" /> : null}
      </button>

      {tenant ? (
        <>
          <span className={s.tsep} aria-hidden="true" />
          <span className={s.tenant}>
            <Icon name="cloud" size="sm" />
            <span className={s.tenantText}>
              <b>{tenant.cliente}</b>
              <span>{tenant.ambiente}</span>
            </span>
          </span>
        </>
      ) : null}

      {user ? (
        <>
          <span className={s.tsep} aria-hidden="true" />
          <div className={s.userWrap} ref={userWrapRef}>
            <button
              type="button"
              className={s.userbox}
              onClick={() => setMenuAberto((open) => !open)}
              aria-label={t('common.ui.account', { name: user.nome })}
              aria-haspopup="menu"
              aria-expanded={menuAberto}
            >
              <span className={s.userText}>
                <b>{user.nome}</b>
                <span>{user.papel}</span>
              </span>
              <span className={s.av} aria-hidden="true">
                {initials(user.nome)}
              </span>
            </button>

            {menuAberto ? (
              <div className={s.usermenu} role="menu" aria-label={t('common.ui.userMenu')}>
                <div className={s.umHead}>
                  <b>{user.nome}</b>
                  {user.email ? <span className={s.umEmail}>{user.email}</span> : null}
                  <span className={s.umPapel}>{user.papel}</span>
                </div>

                {user.googleVinculado === undefined ? null : (
                  <p className={s.umRow}>
                    <Icon
                      name={user.googleVinculado ? 'check-circle' : 'ban'}
                      size="xs"
                      aria-hidden="true"
                    />
                    {t(
                      user.googleVinculado ? 'common.ui.googleLinked' : 'common.ui.googleUnlinked',
                    )}
                  </p>
                )}

                <SeletorDeTema />
                <SeletorDeIdioma />

                {onLogout ? (
                  <button
                    type="button"
                    role="menuitem"
                    className={s.umItem}
                    onClick={onLogout}
                    disabled={logoutPending}
                  >
                    <Icon name="power" size="sm" aria-hidden="true" />
                    {t(logoutPending ? 'common.ui.logout.pending' : 'common.ui.logout')}
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
        </>
      ) : null}
    </header>
  );
}
